import React from "react";
import AppModal from "../AppModal/AppModal";
import { PostApiMethod } from "../../core/services/apiService";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { setFetchedCustomerData, clearFetchedCustomerData } from '../../core/redux/settingsSlice';
import { useAppSelector, useAppDispatch } from "../../core/redux/hooks";
interface VideoCallCustomerDetailsProps {
    isOpen: boolean;
    onClose: (cancelledDuringCall?: boolean) => void;
    callSid?: string;
    locationId?: string;
    countdown?: number;
}

export interface VideoCallCustomerDetailsRef {
    autoSubmit: () => Promise<void>;
    getIsSubmitted: () => boolean;
    getIsCancelledDuringCall: () => boolean;
    resetCancelledDuringCall: () => void;
}

const VideoCallCustomerDetails = React.forwardRef<VideoCallCustomerDetailsRef, VideoCallCustomerDetailsProps>((
    { isOpen, onClose, callSid, locationId, countdown = 0 },
    ref
) => {
    const dispatch = useAppDispatch();
    const fetchedCustomerData = useAppSelector((state: any) => state.settings.fetchedCustomerData);
    const LoginUserDetails = useAppSelector((state: any) => state.auth.users);

    const [customerType, setCustomerType] = React.useState<"new" | "existing">("existing");
    const [tenantDetails, setTenantDetails] = React.useState({
        tenantId: "",
        firstName: "",
        lastName: "",
        countryId: "1",
        facilityId: "",
        dateNeeded: "",
        advSource: "",
        classification: "",
        inquiryDetails: "",
        reservationDetails: "",
        rentalDetails: "",
    });
    const [dateNeeded, setDateNeeded] = React.useState<Date | null>(null);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [isFetching, setIsFetching] = React.useState(false);
    const [customerOptions, setCustomerOptions] = React.useState<Array<{ value: string, label: string }>>([]);
    const [selectedCustomer, setSelectedCustomer] = React.useState("");
    const [selectedCustomerRecord, setSelectedCustomerRecord] = React.useState<any>(null);
    const [originalCustomerRecord, setOriginalCustomerRecord] = React.useState<any>(null);
    const [showVideoNoDataMessage, setShowVideoNoDataMessage] = React.useState(false);
    const [showNoCustomerFoundMessage, setShowNoCustomerFoundMessage] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [apiError, setApiError] = React.useState<string>("");
    const [previousCallSid, setPreviousCallSid] = React.useState<string | undefined>(undefined);
    const [searchParams, setSearchParams] = React.useState<{ firstName: string; lastName: string } | null>(null);
    const [isCancelledDuringCall, setIsCancelledDuringCall] = React.useState(false);

    // FIX Issue #6: Reset isSubmitted when callSid changes (new call)
    React.useEffect(() => {
        setIsSubmitted(false);
        setApiError("");
        setIsCancelledDuringCall(false); // Reset cancelled flag for new call
    }, [callSid]);

    // Clear all state when call ends (callSid changes or becomes undefined)
    React.useEffect(() => {
        // Check if call has ended (callSid changed from a value to undefined or to a different value)
        if (previousCallSid && previousCallSid !== callSid) {
            console.log('Call ended, clearing all customer data');

            // Clear Redux store
            dispatch(clearFetchedCustomerData());

            // Reset all local state
            const defaultDate = new Date();
            defaultDate.setDate(defaultDate.getDate() + 10);
            setDateNeeded(defaultDate);
            setCustomerType("existing");
            setTenantDetails({
                tenantId: "",
                firstName: "",
                lastName: "",
                countryId: "1",
                facilityId: "",
                dateNeeded: defaultDate.toISOString().split("T")[0],
                advSource: "",
                classification: "",
                inquiryDetails: "",
                reservationDetails: "",
                rentalDetails: "",
            });
            setErrors({});
            setIsFetching(false);
            setCustomerOptions([]);
            setSelectedCustomer("");
            setSelectedCustomerRecord(null);
            setOriginalCustomerRecord(null);
            setShowVideoNoDataMessage(false);
            setShowNoCustomerFoundMessage(false);
            setIsSubmitted(false);
            setIsSubmitting(false);
            setApiError("");
            setSearchParams(null);
        }

        // Update previous callSid for next comparison
        setPreviousCallSid(callSid);
    }, [callSid, previousCallSid, dispatch]);

    // Initialize when modal opens (but don't clear if during same call)
    React.useEffect(() => {
        if (isOpen) {
            // Check if we have fetched data from the same call
            const hasFetchedData = fetchedCustomerData && fetchedCustomerData.length > 0;

            if (hasFetchedData && searchParams) {
                // Restore the search form with previous firstName and lastName
                setTenantDetails(prev => ({
                    ...prev,
                    firstName: searchParams.firstName,
                    lastName: searchParams.lastName,
                }));

                // Rebuild customer options from combined data
                // Note: fetchedCustomerData[0] now contains all combined data from all records
                const options: Array<{ value: string, label: string }> = [];
                const uniqueCombinations = new Set<string>();

                const combinedRecord = fetchedCustomerData[0];
                const firstName = combinedRecord.FirstName || "";
                const lastName = combinedRecord.LastName || "";

                // Process all combined inquiries
                if (combinedRecord?.Inquiries && combinedRecord?.Inquiries?.length > 0) {
                    combinedRecord.Inquiries.forEach((inquiry: any) => {
                        const unitNumber = inquiry.UnitNumber || "N/A";
                        const combination = `${firstName}-${lastName}-${unitNumber}-Inquiry`;
                        if (!uniqueCombinations.has(combination)) {
                            uniqueCombinations.add(combination);
                            options.push({
                                value: combination,
                                label: `${firstName} ${lastName} - Unit ${unitNumber} - Inquiry`
                            });
                        }
                    });
                }

                // Process all combined reservations
                if (combinedRecord?.Reservations && combinedRecord.Reservations?.length > 0) {
                    combinedRecord.Reservations.forEach((reservation: any) => {
                        const unitNumber = reservation.UnitNumber || "N/A";
                        const combination = `${firstName}-${lastName}-${unitNumber}-Reservation`;
                        if (!uniqueCombinations.has(combination)) {
                            uniqueCombinations.add(combination);
                            options.push({
                                value: combination,
                                label: `${firstName} ${lastName} - Unit ${unitNumber} - Reservation`
                            });
                        }
                    });
                }

                // Process all combined rentals
                if (combinedRecord?.Rentals && combinedRecord?.Rentals?.length > 0) {
                    combinedRecord.Rentals.forEach((rental: any) => {
                        const unitNumber = rental.UnitNumber || "N/A";
                        const combination = `${firstName}-${lastName}-${unitNumber}-Rental`;
                        if (!uniqueCombinations.has(combination)) {
                            uniqueCombinations.add(combination);
                            options.push({
                                value: combination,
                                label: `${firstName} ${lastName} - Unit ${unitNumber} - Rental`
                            });
                        }
                    });
                }

                setCustomerOptions(options);
            } else if (!hasFetchedData) {
                // First open or new call - reset everything
                const defaultDate = new Date();
                defaultDate.setDate(defaultDate.getDate() + 10);
                setDateNeeded(defaultDate);

                setCustomerType("existing");
                setTenantDetails({
                    tenantId: "",
                    firstName: "",
                    lastName: "",
                    countryId: "1",
                    facilityId: "",
                    dateNeeded: defaultDate.toISOString().split("T")[0],
                    advSource: "",
                    classification: "",
                    inquiryDetails: "",
                    reservationDetails: "",
                    rentalDetails: "",
                });
                setIsSubmitted(false);
                setIsSubmitting(false);
                setApiError("");
                setErrors({});
                setCustomerOptions([]);
            }
        }
    }, [isOpen, fetchedCustomerData, searchParams]);

    const handleDateChange = (date: Date | null) => {
        setDateNeeded(date);
        setTenantDetails({
            ...tenantDetails,
            dateNeeded: date ? date.toISOString().split("T")[0] : ""
        });
    };

    const handleFetchCustomer = async () => {
        if (!tenantDetails.firstName.trim() || !tenantDetails.lastName.trim()) {
            setErrors({
                firstName: !tenantDetails.firstName.trim() ? "First Name is required" : "",
                lastName: !tenantDetails.lastName.trim() ? "Last Name is required" : ""
            });
            return;
        }
        setErrors({});
        setIsFetching(true);
        try {
            const fetchPayload = {
                firstName: tenantDetails.firstName.trim(),
                lastName: tenantDetails.lastName.trim(),
                locationId: locationId,
                action: "fetch"
            };
            const res: any = await PostApiMethod("tenant/sendCallSummary/fetchCustomerDetails", fetchPayload);
            console.log("Fetch API Response:", res);

            // Store the API result
            const apiData = res?.data?.data || [];
            dispatch(setFetchedCustomerData(apiData));

            // Save search parameters for restoring later
            setSearchParams({
                firstName: tenantDetails.firstName.trim(),
                lastName: tenantDetails.lastName.trim()
            });

            // Check if no customer data found
            if (apiData.length === 0) {
                setShowNoCustomerFoundMessage(true);
                setCustomerOptions([]);
                return;
            }

            setShowNoCustomerFoundMessage(false);

            // Combine all Inquiries, Reservations, and Rentals from ALL records (like voice call flow)
            const allInquiries = apiData.flatMap((record: any) => record.Inquiries || []);
            const allReservations = apiData.flatMap((record: any) => record.Reservations || []);
            const allRentals = apiData.flatMap((record: any) => record.Rentals || []);

            // Use first record for basic customer info
            const firstCustomer = apiData[0];
            const firstName = firstCustomer.FirstName || "";
            const lastName = firstCustomer.LastName || "";

            // Create a combined customer object with ALL data from ALL records
            const combinedCustomer = {
                ...firstCustomer,
                Inquiries: allInquiries,
                Reservations: allReservations,
                Rentals: allRentals
            };

            // Update Redux with combined data (store as single record array)
            dispatch(setFetchedCustomerData([combinedCustomer]));

            // Check if combined customer has any data
            const hasData = allInquiries.length > 0 || allReservations.length > 0 || allRentals.length > 0;

            if (!hasData) {
                // Customer found but has no data - show "no data" message
                setShowVideoNoDataMessage(true);
                setCustomerOptions([]);
                return;
            }

            // Process combined data to create unit-specific options with type
            const options: Array<{ value: string, label: string }> = [];
            const uniqueCombinations = new Set<string>();

            // Process all combined inquiries
            allInquiries.forEach((inquiry: any) => {
                const unitNumber = inquiry.UnitNumber || "N/A";
                const combination = `${firstName}-${lastName}-${unitNumber}-Inquiry`;

                if (!uniqueCombinations.has(combination)) {
                    uniqueCombinations.add(combination);
                    options.push({
                        value: combination,
                        label: `${firstName} ${lastName} - Unit ${unitNumber} - Inquiry`
                    });
                }
            });

            // Process all combined reservations
            allReservations.forEach((reservation: any) => {
                const unitNumber = reservation.UnitNumber || "N/A";
                const combination = `${firstName}-${lastName}-${unitNumber}-Reservation`;

                if (!uniqueCombinations.has(combination)) {
                    uniqueCombinations.add(combination);
                    options.push({
                        value: combination,
                        label: `${firstName} ${lastName} - Unit ${unitNumber} - Reservation`
                    });
                }
            });

            // Process all combined rentals
            allRentals.forEach((rental: any) => {
                const unitNumber = rental.UnitNumber || "N/A";
                const combination = `${firstName}-${lastName}-${unitNumber}-Rental`;

                if (!uniqueCombinations.has(combination)) {
                    uniqueCombinations.add(combination);
                    options.push({
                        value: combination,
                        label: `${firstName} ${lastName} - Unit ${unitNumber} - Rental`
                    });
                }
            });

            setCustomerOptions(options);
        } catch (err) {
            console.error("Fetch API Error:", err);
        } finally {
            setIsFetching(false);
        }
    };


    const handlePrimaryButtonClick = () => {
        const formElement = document.getElementById("video-customer-details-form") as HTMLFormElement;
        if (formElement) {
            formElement.requestSubmit();
        }
    };

    // Handle cancel button: Submit immediately if countdown is active (after call ends)
    const handleCancelClick = async () => {
        // During call (countdown = 0): Just close popup and mark as cancelled
        if (countdown === 0) {
            setIsCancelledDuringCall(true); // Track that user cancelled during call
            onClose(true); // Pass true to indicate cancellation during call
            return;
        }

        // After call ends (countdown > 0): Submit immediately then close
        // Double-submit prevention: Check if already submitted or submitting
        if (isSubmitted || isSubmitting) {
            onClose(false);
            return;
        }

        // Submit immediately
        setIsSubmitting(true);
        try {
            await handleTenantDetailsSubmit(undefined, true);
            // isSubmitted will be set to true in handleTenantDetailsSubmit on success
        } catch (error) {
            console.error("Submit on cancel failed (video):", error);
            // Still close even if submit fails
        } finally {
            setIsSubmitting(false);
            onClose(false);
        }
    };

    const handleTenantDetailsSubmit = async (e?: React.FormEvent, isAutoSubmit: boolean = false) => {
        if (e) e.preventDefault();

        // Prevent duplicate submissions
        if (isSubmitted || isSubmitting) return;

        // Clear previous errors
        setErrors({});
        setApiError("");

        const newErrors: { [key: string]: string } = {};

        // Get facilityId from fetchedCustomerData (now always a single combined record)
        const getfacilityId = () => {
            const customerRecord = fetchedCustomerData?.[0];
            if (customerRecord?.facilityId) {
                return customerRecord.facilityId;
            }
            return null;
        };

        if (customerType === "new") {
            // New customer - video call
            if (!isAutoSubmit) {
                if (!tenantDetails.firstName.trim()) newErrors.firstName = "First Name is required";
                if (!tenantDetails.lastName.trim()) newErrors.lastName = "Last Name is required";
                if (!dateNeeded) newErrors.dateNeeded = "Date Needed is required";
                if (!tenantDetails.advSource) newErrors.advSource = "Please select how you heard about us";

                setErrors(newErrors);
                if (Object.keys(newErrors).length > 0) return;
            }

            const newCustomerPayload = {
                tenantId: tenantDetails.tenantId.trim() || 0,
                firstName: tenantDetails.firstName.trim(),
                lastName: tenantDetails.lastName.trim(),
                facilityId: getfacilityId(),
                countryId: tenantDetails.countryId,
                dateNeeded: dateNeeded ? dateNeeded.toISOString().split("T")[0] : tenantDetails.dateNeeded,
                advSource: tenantDetails.advSource,
                customerType: "new",
                callType: "video",
                callSid: callSid,
                locationId: locationId,
                agentName: LoginUserDetails?.name || "",
            };

            setIsSubmitting(true);
            try {
                console.log(newCustomerPayload, 'newCustomerPayload video ==============>');
                const res = await PostApiMethod("tenant/sendCallSummary/postCustomerDetails", newCustomerPayload);
                console.log("New Customer API Response:", res);
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } catch (err) {
                console.error("New Customer API Error:", err);
                setApiError(err instanceof Error ? err.message : "Failed to submit customer details. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        } else {
            // Existing customer - Video call
            if (!isAutoSubmit) {
                if (!tenantDetails.firstName.trim()) newErrors.firstName = "First Name is required";
                if (!tenantDetails.lastName.trim()) newErrors.lastName = "Last Name is required";

                setErrors(newErrors);
                if (Object.keys(newErrors).length > 0) return;
            }

            // Use the appropriate customer data source
            let customer = selectedCustomerRecord || originalCustomerRecord;

            // If no customer card selected but we have fetched data, use the combined record
            if (!customer && fetchedCustomerData?.length > 0) {
                // fetchedCustomerData[0] already contains all combined data from all records
                customer = fetchedCustomerData[0];
            }

            // Smart default logic for classification and details
            let finalClassification = tenantDetails.classification;
            let finalInquiryNo = tenantDetails.inquiryDetails;
            let finalReservationNumber = tenantDetails.reservationDetails;
            let finalLeaseNumber = tenantDetails.rentalDetails;

            // If no classification selected, use priority: Inquiry > Reservation > Rental
            if (!finalClassification) {
                if (customer?.Inquiries?.length > 0) {
                    finalClassification = "Inquiry";
                } else if (customer?.Reservations?.length > 0) {
                    finalClassification = "Reservation";
                } else if (customer?.Rentals?.length > 0) {
                    finalClassification = "Rental";
                }
            }

            // Apply default first record logic for each classification
            if (finalClassification === "Inquiry") {
                // If no inquiry selected, use first inquiry record
                if (!finalInquiryNo && customer?.Inquiries?.length > 0) {
                    finalInquiryNo = customer.Inquiries[0].InquiryNumber;
                }
            } else if (finalClassification === "Reservation") {
                // If no reservation selected, use first reservation record
                if (!finalReservationNumber && customer?.Reservations?.length > 0) {
                    finalReservationNumber = customer.Reservations[0].ReservationNumber;
                }
            } else if (finalClassification === "Rental") {
                // If no rental selected, use first rental record
                if (!finalLeaseNumber && customer?.Rentals?.length > 0) {
                    finalLeaseNumber = customer.Rentals[0].LeaseNumber;
                }
            }

            let videoPayload;

            // Get customer names from form or fetched data
            const customerFirstName = tenantDetails.firstName.trim() || customer?.FirstName || "";
            const customerLastName = tenantDetails.lastName.trim() || customer?.LastName || "";

            // Build payload based on final classification with specific field order
            if (finalClassification === "Inquiry") {
                videoPayload = {
                    facilityId: getfacilityId(),
                    InquiryNo: finalInquiryNo,
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            } else if (finalClassification === "Reservation") {
                videoPayload = {
                    facilityId: getfacilityId(),
                    ReservationNumber: finalReservationNumber,
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            } else if (finalClassification === "Rental") {
                // Find the selected rental to get its TenantId (not the top-level customer TenantID)
                const selectedRental = customer?.Rentals?.find((rental: any) => rental.LeaseNumber === finalLeaseNumber);

                videoPayload = {
                    TenantId: selectedRental?.TenantId?.toString() || customer?.TenantID?.toString() || "",
                    LeaseNumber: finalLeaseNumber,
                    facilityId: getfacilityId(),
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            } else {
                videoPayload = {
                    classification: finalClassification,
                    customerType: "existing",
                    callType: "video",
                    locationId: locationId,
                    callSid: callSid,
                    facilityId: getfacilityId(),
                    agentName: LoginUserDetails?.name || "",
                    customerFirstName: customerFirstName,
                    customerLastName: customerLastName
                };
            }

            setIsSubmitting(true);
            try {
                console.log(videoPayload, 'videoPayload ==============>');
                const res = await PostApiMethod("tenant/sendCallSummary/postCustomerDetails", videoPayload);
                console.log("Video Existing Customer API Response:", res);
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } catch (err) {
                console.error("Video Existing Customer API Error:", err);
                setApiError(err instanceof Error ? err.message : "Failed to submit customer details. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    // Expose autoSubmit function and isSubmitted state via ref
    React.useImperativeHandle(ref, () => ({
        autoSubmit: async () => {
            if (!isSubmitted) {
                await handleTenantDetailsSubmit(undefined, true);
            }
        },
        getIsSubmitted: () => isSubmitted,
        getIsCancelledDuringCall: () => isCancelledDuringCall,
        resetCancelledDuringCall: () => setIsCancelledDuringCall(false)
    }));

    if (!isOpen) return null;

    return (
        <AppModal
            title="Customer Details"
            bodyText=""
            primaryButtonLabel={isSubmitting ? "Submitting..." : "Submit"}
            secondaryButtonLabel="Cancel"
            primaryButtonEventEmitter={handlePrimaryButtonClick}
            secondaryButtonEventEmitter={handleCancelClick}
            stayAppear={true}
            disablePrimaryButton={showVideoNoDataMessage || showNoCustomerFoundMessage || isSubmitting}
        >
            <form id="video-customer-details-form" onSubmit={handleTenantDetailsSubmit} style={{ position: 'relative' }}>
                {/* Countdown Timer Message */}
                {countdown > 0 && (
                    <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert" style={{ borderLeft: '4px solid #ff9800' }}>
                        <div className="d-flex align-items-center">
                            <i className="bi bi-clock-fill me-2" style={{ fontSize: '1.5rem' }}></i>
                            <div>
                                <strong>Auto-submitting in {countdown} second{countdown !== 1 ? 's' : ''}...</strong>
                                <br />
                                <small className="text-muted">Fill in the details or submit now to avoid auto-submission</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* API Error Message */}
                {apiError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>Error:</strong> {apiError}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setApiError("")}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {/* Elegant Loading Overlay - Only for Modal */}
                {isSubmitting && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 1050,
                            backdropFilter: 'blur(4px)',
                            borderRadius: '0.5rem'
                        }}
                    >
                        <div className="text-center">
                            <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem', borderWidth: '0.3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-3 text-white fw-medium">Processing...</div>
                        </div>
                    </div>
                )}

                {/* Beautiful Header with Toggle Button */}
                <div className="mb-4">
                    {customerType === "existing" ? (
                        <div className="bg-gradient-primary rounded-3 p-4 text-white position-relative" style={{ background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center">
                                        {/* <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                                            <i className="bi bi-search" style={{ fontSize: '1.2rem' }}></i>
                                        </div> */}
                                        <h5 className="mb-0 fw-bold">Find Existing Customer</h5>
                                    </div>
                                    <small className="mb-0 opacity-90">Search by customer name to find existing records</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm d-flex align-items-center shadow-sm"
                                    onClick={() => {
                                        setCustomerType("new");
                                        setShowVideoNoDataMessage(false);
                                    }}
                                    title="Register New Customer"
                                >
                                    <i className="bi bi-person-plus me-1"></i>
                                    New Customer
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gradient-success rounded-3 p-4 text-white position-relative" style={{ background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="d-flex align-items-center mb-2">
                                        {/* <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
                                            <i className="bi bi-person-plus" style={{ fontSize: '1.2rem' }}></i>
                                        </div> */}
                                        <h5 className="mb-0 fw-bold">New Customer Registration</h5>
                                    </div>
                                    <small className="mb-0 opacity-90">Fill in the details to register a new customer</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm d-flex align-items-center shadow-sm"
                                    onClick={() => {
                                        setCustomerType("existing");
                                        setShowVideoNoDataMessage(false);
                                    }}
                                    title="Search Existing Customer"
                                >
                                    <i className="bi bi-search me-1"></i>
                                    Find Existing
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Video Call - Existing Customer - Only First Name and Last Name */}
                {customerType === "existing" && (
                    <>
                        <div className="row mb-3">
                            <div className="col-5">
                                <label className="form-label fw-semibold bg-light px-2 py-1 rounded">
                                    First Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="firstName"
                                    className="form-control"
                                    value={tenantDetails.firstName}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, firstName: e.target.value })}
                                />
                                {errors.firstName && <small className="text-danger">{errors.firstName}</small>}
                            </div>
                            <div className="col-5">
                                <label className="form-label fw-semibold bg-light px-2 py-1 rounded">
                                    Last Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    className="form-control"
                                    value={tenantDetails.lastName}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, lastName: e.target.value })}
                                />
                                {errors.lastName && <small className="text-danger">{errors.lastName}</small>}
                            </div>
                            <div className="col-2 text-center" style={{ paddingTop: "40px" }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleFetchCustomer}
                                    disabled={isFetching}
                                >
                                    {isFetching ? "Fetching..." : "Fetch"}
                                </button>
                            </div>
                        </div>

                        {/* No customer found message */}
                        {showNoCustomerFoundMessage && (
                            <div className="row mb-3 justify-content-center">
                                <div className="col-lg-10">
                                    <div className="card border-danger shadow-sm">
                                        <div className="card-body text-center p-3">
                                            <div className="mb-2">
                                                <i className="bi bi-search text-danger" style={{ fontSize: '2.5rem' }}></i>
                                            </div>
                                            <h6 className="card-title text-danger mb-2">No Customer Found</h6>
                                            <p className="card-text text-muted mb-3">
                                                No customer found with the provided name.
                                                <br />
                                                <small>Please verify the spelling or proceed with the new customer process.</small>
                                            </p>
                                            <button
                                                type="button"
                                                className="btn btn-primary px-3"
                                                onClick={() => {
                                                    setCustomerType("new");
                                                    setShowNoCustomerFoundMessage(false);
                                                }}
                                            >
                                                <i className="bi bi-person-plus me-2"></i>
                                                Create New Customer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Customer Cards */}
                        {customerOptions.length > 0 && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-people text-primary me-2"></i>
                                        Select Customer
                                    </label>
                                    <div className="row g-3">
                                        {customerOptions.map((option, index) => {
                                            const parts = option.value.split('-');
                                            const firstName = parts[0];
                                            const lastName = parts[1];
                                            const unitNumber = parts[2];
                                            const recordType = parts[3];
                                            const isSelected = selectedCustomer === option.value;

                                            return (
                                                <div key={index} className="col-md-6 col-lg-3">
                                                    <div
                                                        className={`card h-100 ${isSelected ? 'border-2 border-primary bg-primary bg-opacity-10 shadow' : 'border border-secondary'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease-in-out',
                                                            transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                e.currentTarget.style.boxShadow = '';
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            // Toggle logic: if already selected, unselect it
                                                            if (isSelected) {
                                                                setSelectedCustomer("");
                                                                setSelectedCustomerRecord(null);
                                                                setOriginalCustomerRecord(null);
                                                                setTenantDetails({ ...tenantDetails, classification: "" });
                                                                return;
                                                            }

                                                            setSelectedCustomer(option.value);

                                                            let matchingRecords = fetchedCustomerData.filter((record: any) =>
                                                                record.FirstName === firstName && record.LastName === lastName
                                                            );

                                                            const combinedRecord = matchingRecords.length > 0 ? {
                                                                ...matchingRecords[0],
                                                                Rentals: matchingRecords.flatMap((record: any) => record.Rentals || []),
                                                                Inquiries: matchingRecords.flatMap((record: any) => record.Inquiries || []),
                                                                Reservations: matchingRecords.flatMap((record: any) => record.Reservations || [])
                                                            } : null;

                                                            if (!combinedRecord) {
                                                                setShowVideoNoDataMessage(true);
                                                                setOriginalCustomerRecord(null);
                                                                setSelectedCustomerRecord(null);
                                                                return;
                                                            }

                                                            setShowVideoNoDataMessage(false);
                                                            setOriginalCustomerRecord(combinedRecord);

                                                            let filteredRecord = { ...combinedRecord };

                                                            if (recordType === 'Inquiry') {
                                                                filteredRecord.Inquiries = combinedRecord.Inquiries?.filter((inquiry: any) =>
                                                                    String(inquiry.UnitNumber) === String(unitNumber)
                                                                ) || [];
                                                                filteredRecord.Rentals = [];
                                                                filteredRecord.Reservations = [];
                                                            } else if (recordType === 'Reservation') {
                                                                filteredRecord.Reservations = combinedRecord.Reservations?.filter((reservation: any) =>
                                                                    String(reservation.UnitNumber) === String(unitNumber)
                                                                ) || [];
                                                                filteredRecord.Inquiries = [];
                                                                filteredRecord.Rentals = [];
                                                            } else if (recordType === 'Rental') {
                                                                filteredRecord.Rentals = combinedRecord.Rentals?.filter((rental: any) =>
                                                                    String(rental.UnitNumber) === String(unitNumber)
                                                                ) || [];
                                                                filteredRecord.Inquiries = [];
                                                                filteredRecord.Reservations = [];
                                                            }

                                                            setSelectedCustomerRecord(filteredRecord);

                                                            if (recordType === 'Inquiry' || recordType === 'Reservation' || recordType === 'Rental') {
                                                                setTenantDetails({ ...tenantDetails, classification: recordType });
                                                            } else {
                                                                setTenantDetails({ ...tenantDetails, classification: "" });
                                                            }
                                                        }}
                                                    >
                                                        {isSelected && (
                                                            <div className="position-absolute top-0 end-0 p-1">
                                                                <i className="bi bi-check-circle-fill text-primary bg-white rounded-circle" style={{ fontSize: '1.1rem' }}></i>
                                                            </div>
                                                        )}
                                                        <div className="card-body p-2 text-center">
                                                            <div className="mb-1">
                                                                <i className={`bi bi-person-circle ${isSelected ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.5rem' }}></i>
                                                            </div>
                                                            <h6 className={`card-title mb-1 ${isSelected ? 'fw-bold' : ''}`} style={{ fontSize: '0.85rem' }}>
                                                                {firstName} {lastName}
                                                            </h6>
                                                            <p className="card-text mb-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                                                <i className="bi bi-geo-alt me-1"></i>
                                                                {unitNumber}
                                                            </p>
                                                            <span className={`badge ${recordType === 'Inquiry' ? 'bg-info' : recordType === 'Reservation' ? 'bg-warning text-dark' : recordType === 'Rental' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>
                                                                {recordType}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* No data message for video existing customers */}
                        {showVideoNoDataMessage && (
                            <div className="row mb-3 justify-content-center">
                                <div className="col-lg-10">
                                    <div className="card border-warning shadow-sm">
                                        <div className="card-body text-center p-3">
                                            <div className="mb-2">
                                                <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '2.5rem' }}></i>
                                            </div>
                                            <h6 className="card-title text-warning mb-2">No Customer Data Found</h6>
                                            <p className="card-text text-muted mb-3">
                                                This customer doesn't have any inquiry, reservation, or rental records.
                                                <br />
                                                <small>You can proceed with the new customer process using their existing details.</small>
                                            </p>
                                            <button
                                                type="button"
                                                className="btn btn-primary px-3"
                                                onClick={() => {
                                                    setCustomerType("new");
                                                    setShowVideoNoDataMessage(false);
                                                    const customerRecord = fetchedCustomerData?.[0];
                                                    if (customerRecord?.TenantID) {
                                                        setTenantDetails({
                                                            ...tenantDetails,
                                                            tenantId: customerRecord.TenantID.toString()
                                                        });
                                                    }
                                                }}
                                            >
                                                <i className="bi bi-person-plus me-2"></i>
                                                Continue as New Customer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Call Purpose - Interactive Cards */}
                        {/* {originalCustomerRecord && !tenantDetails.classification && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-2 py-1 rounded mb-3">What can we help you with today?</label>
                                    <div className="row g-3">
                                        {originalCustomerRecord.Inquiries?.length > 0 && (
                                            <div className="col-md-4">
                                                <div
                                                    className={`card h-100 cursor-pointer border-2 ${tenantDetails.classification === 'Inquiry' ? 'border-info bg-info bg-opacity-10' : 'border-light'} hover-shadow`}
                                                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                                                    onClick={() => setTenantDetails({ ...tenantDetails, classification: 'Inquiry' })}
                                                    onMouseEnter={(e) => {
                                                        if (tenantDetails.classification !== 'Inquiry') {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,123,255,0.15)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (tenantDetails.classification !== 'Inquiry') {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body text-center p-3">
                                                        <div className={`mb-2 ${tenantDetails.classification === 'Inquiry' ? 'text-info' : 'text-muted'}`}>
                                                            <i className="bi bi-question-circle-fill" style={{ fontSize: '2.5rem' }}></i>
                                                        </div>
                                                        <h6 className={`card-title mb-2 ${tenantDetails.classification === 'Inquiry' ? 'text-info fw-bold' : 'text-dark'}`}>
                                                            Inquiry
                                                        </h6>
                                                        <p className="card-text text-muted small mb-0">
                                                            Questions about units, pricing, or availability
                                                        </p>
                                                        {tenantDetails.classification === 'Inquiry' && (
                                                            <div className="mt-2">
                                                                <i className="bi bi-check-circle-fill text-info"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {originalCustomerRecord.Reservations?.length > 0 && (
                                            <div className="col-md-4">
                                                <div
                                                    className={`card h-100 cursor-pointer border-2 ${tenantDetails.classification === 'Reservation' ? 'border-warning bg-warning bg-opacity-10' : 'border-light'} hover-shadow`}
                                                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                                                    onClick={() => setTenantDetails({ ...tenantDetails, classification: 'Reservation' })}
                                                    onMouseEnter={(e) => {
                                                        if (tenantDetails.classification !== 'Reservation') {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,193,7,0.15)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (tenantDetails.classification !== 'Reservation') {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body text-center p-3">
                                                        <div className={`mb-2 ${tenantDetails.classification === 'Reservation' ? 'text-warning' : 'text-muted'}`}>
                                                            <i className="bi bi-calendar-check-fill" style={{ fontSize: '2.5rem' }}></i>
                                                        </div>
                                                        <h6 className={`card-title mb-2 ${tenantDetails.classification === 'Reservation' ? 'text-warning fw-bold' : 'text-dark'}`}>
                                                            Reservation
                                                        </h6>
                                                        <p className="card-text text-muted small mb-0">
                                                            Manage existing reservations or bookings
                                                        </p>
                                                        {tenantDetails.classification === 'Reservation' && (
                                                            <div className="mt-2">
                                                                <i className="bi bi-check-circle-fill text-warning"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {originalCustomerRecord.Rentals?.length > 0 && (
                                            <div className="col-md-4">
                                                <div
                                                    className={`card h-100 cursor-pointer border-2 ${tenantDetails.classification === 'Rental' ? 'border-success bg-success bg-opacity-10' : 'border-light'} hover-shadow`}
                                                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                                                    onClick={() => setTenantDetails({ ...tenantDetails, classification: 'Rental' })}
                                                    onMouseEnter={(e) => {
                                                        if (tenantDetails.classification !== 'Rental') {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(40,167,69,0.15)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (tenantDetails.classification !== 'Rental') {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '';
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body text-center p-3">
                                                        <div className={`mb-2 ${tenantDetails.classification === 'Rental' ? 'text-success' : 'text-muted'}`}>
                                                            <i className="bi bi-house-fill" style={{ fontSize: '2.5rem' }}></i>
                                                        </div>
                                                        <h6 className={`card-title mb-2 ${tenantDetails.classification === 'Rental' ? 'text-success fw-bold' : 'text-dark'}`}>
                                                            Rental
                                                        </h6>
                                                        <p className="card-text text-muted small mb-0">
                                                            Support for current rental agreements
                                                        </p>
                                                        {tenantDetails.classification === 'Rental' && (
                                                            <div className="mt-2">
                                                                <i className="bi bi-check-circle-fill text-success"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )} */}

                        {/* Inquiry Details */}
                        {selectedCustomerRecord && tenantDetails.classification === "Inquiry" && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-question-circle text-info me-2"></i>
                                        Select Inquiry
                                    </label>
                                    {selectedCustomerRecord.Inquiries?.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedCustomerRecord.Inquiries.map((inquiry: any, index: number) => {
                                                const isSelected = tenantDetails.inquiryDetails === inquiry.InquiryNumber;
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`px-3 py-2 rounded-pill ${isSelected ? 'bg-info text-white' : 'bg-light border'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontSize: '0.9rem'
                                                        }}
                                                        onClick={() => setTenantDetails({
                                                            ...tenantDetails,
                                                            inquiryDetails: isSelected ? "" : inquiry.InquiryNumber
                                                        })}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }
                                                        }}
                                                    >
                                                        <i className="bi bi-question-circle-fill me-2"></i>
                                                        <strong>{inquiry.InquiryNumber || 'Inquiry'}</strong>
                                                        <span className="mx-2">•</span>
                                                        <i className="bi bi-geo-alt me-1"></i>
                                                        Unit {inquiry.UnitNumber || 'N/A'}
                                                        {isSelected && <i className="bi bi-check-circle-fill ms-2"></i>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="bi bi-inbox text-muted" style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
                                            <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>No inquiries available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Reservation Details */}
                        {selectedCustomerRecord && tenantDetails.classification === "Reservation" && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-calendar-check text-warning me-2"></i>
                                        Select Reservation
                                    </label>
                                    {selectedCustomerRecord.Reservations?.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedCustomerRecord.Reservations.map((reservation: any, index: number) => {
                                                const isSelected = tenantDetails.reservationDetails === reservation.ReservationNumber;
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`px-3 py-2 rounded-pill ${isSelected ? 'bg-warning text-dark' : 'bg-light border'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontSize: '0.9rem'
                                                        }}
                                                        onClick={() => setTenantDetails({
                                                            ...tenantDetails,
                                                            reservationDetails: isSelected ? "" : reservation.ReservationNumber
                                                        })}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }
                                                        }}
                                                    >
                                                        <i className="bi bi-calendar-check-fill me-2"></i>
                                                        <strong>{reservation.ReservationNumber}</strong>
                                                        <span className="mx-2">•</span>
                                                        <i className="bi bi-geo-alt me-1"></i>
                                                        Unit {reservation.UnitNumber || 'N/A'}
                                                        {isSelected && <i className="bi bi-check-circle-fill ms-2"></i>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="bi bi-calendar-x text-muted" style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
                                            <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>No reservations available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Rental Details */}
                        {selectedCustomerRecord && tenantDetails.classification === "Rental" && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                        <i className="bi bi-house text-success me-2"></i>
                                        Select Rental
                                    </label>
                                    {selectedCustomerRecord.Rentals?.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedCustomerRecord.Rentals.map((rental: any, index: number) => {
                                                const isSelected = tenantDetails.rentalDetails === rental.LeaseNumber;
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`px-3 py-2 rounded-pill ${isSelected ? 'bg-success text-white' : 'bg-light border'}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontSize: '0.9rem'
                                                        }}
                                                        onClick={() => setTenantDetails({
                                                            ...tenantDetails,
                                                            rentalDetails: isSelected ? "" : rental.LeaseNumber
                                                        })}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.backgroundColor = '';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }
                                                        }}
                                                    >
                                                        <i className="bi bi-house-fill me-2"></i>
                                                        <strong>{rental.LeaseNumber || `REN${index + 1}`}</strong>
                                                        <span className="mx-2">•</span>
                                                        <i className="bi bi-geo-alt me-1"></i>
                                                        Unit {rental.UnitNumber || 'N/A'}
                                                        {isSelected && <i className="bi bi-check-circle-fill ms-2"></i>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="bi bi-house-x text-muted" style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
                                            <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>No rentals available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Show all fields for new customers */}
                {customerType === "new" && (
                    <div className="mb-3">
                        {/* Tenant ID & Country ID */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="tenantId"
                                        id="tenantId"
                                        className={`form-control ${errors.tenantId ? 'is-invalid' : ''}`}
                                        value={tenantDetails.tenantId}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, tenantId: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Tenant ID"
                                    />
                                    <label htmlFor="tenantId">
                                        <i className="bi bi-person-badge text-primary me-2"></i>
                                        Tenant ID
                                    </label>
                                    {errors.tenantId && <div className="invalid-feedback">{errors.tenantId}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="countryId"
                                        id="countryId"
                                        className="form-control"
                                        value={tenantDetails.countryId}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, countryId: e.target.value })}
                                        placeholder="Country ID"
                                        disabled={isSubmitting}
                                    />
                                    <label htmlFor="countryId">
                                        <i className="bi bi-globe text-primary me-2"></i>
                                        Country Code
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* First & Last Name */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="firstName"
                                        id="firstName"
                                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.firstName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, firstName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="First Name"
                                    />
                                    <label htmlFor="firstName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        First Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="lastName"
                                        id="lastName"
                                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.lastName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, lastName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Last Name"
                                    />
                                    <label htmlFor="lastName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        Last Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Date Needed & Advertising Source */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label htmlFor="dateNeeded" className="form-label text-muted small mb-2">
                                    <i className="bi bi-calendar-event text-primary me-1"></i>
                                    Date Needed <span className="text-danger">*</span>
                                </label>
                                <DatePicker
                                    id="dateNeeded"
                                    selected={dateNeeded}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className={`form-control ${errors.dateNeeded ? 'is-invalid' : ''}`}
                                    placeholderText="Select date needed"
                                    minDate={new Date()}
                                    disabled={isSubmitting}
                                />
                                {errors.dateNeeded && <div className="invalid-feedback d-block">{errors.dateNeeded}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="advSource" className="form-label text-muted small mb-2">
                                    <i className="bi bi-megaphone text-primary me-1"></i>
                                    Advertising Source <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.advSource ? 'is-invalid' : ''}`}
                                    id="advSource"
                                    name="advSource"
                                    value={tenantDetails.advSource}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, advSource: e.target.value })}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select...</option>
                                    <option value="Phone">Phone</option>
                                    <option value="Website">Website</option>
                                    <option value="Kiosk">Kiosk</option>
                                    <option value="QR code">QR Code</option>
                                </select>
                                {errors.advSource && <div className="invalid-feedback">{errors.advSource}</div>}
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </AppModal>
    );
});

VideoCallCustomerDetails.displayName = 'VideoCallCustomerDetails';

export default VideoCallCustomerDetails;